import { EventType } from "@/analytics/analytics-events";
import { ToggleFixedStatusForSelectedScans } from "@/analytics/staging-area-workflows-events-properties";
import { selectRevisionEntityDescendantScans } from "@/data-preparation-tool/store/revision/revision-scan-selectors";
import { isEntityCluster } from "@/data-preparation-tool/utils/entity-utils";
import {
  computeFixedStatus,
  toggleFixedStatus,
} from "@/data-preparation-tool/utils/toggle-fixed-status";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  CircularProgress,
  FaroMenuItem,
  FaroMenuItemProps,
  FaroSvgIcon,
  FaroSvgIconProps,
  FaroText,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  CaptureTreeEntityRevision,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { JSX, useCallback, useMemo, useState } from "react";
import { ReactComponent as ReleaseIcon } from "../../../icons/anchor-basic-striketrough.svg";
import { ReactComponent as FixIcon } from "../../../icons/anchor-basic.svg";
import { useFixedScanCount } from "../../revision-contextual-toolbar";

type FixReleaseContextMenuActionItemProps = {
  entity: CaptureTreeEntityRevision;
  disabled?: boolean;
  onSuccess?(): void;
};

/**
 * @returns A context menu item to fix or release a capture.
 */
export function FixReleaseContextMenuActionItem({
  entity,
  disabled,
  onSuccess,
}: FixReleaseContextMenuActionItemProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const descendantScans = useAppSelector(
    selectRevisionEntityDescendantScans(entity.id),
  );
  const isClusterOrRoot = isEntityCluster(entity) || entity.parentId === null;

  const descendantScanIds = useMemo(
    () => descendantScans.map((scan) => scan.id),
    [descendantScans],
  );

  const fixedDescendantCount = useFixedScanCount(descendantScanIds);

  const isFixed = useMemo(() => {
    if (isClusterOrRoot) {
      return computeFixedStatus(fixedDescendantCount, descendantScanIds.length);
    }
    return entity.isFixed === true;
  }, [
    isClusterOrRoot,
    fixedDescendantCount,
    descendantScanIds.length,
    entity.isFixed,
  ]);

  const onToggleFixedStatus = useCallback(async () => {
    if (isProcessing) return;

    const captureIds = isClusterOrRoot ? descendantScanIds : [entity.id];

    Analytics.track<ToggleFixedStatusForSelectedScans>(
      EventType.toggleFixedStatusForSelectedScans,
      {
        via: "scan tree",
        fixed: !isFixed,
        selectedScanCount: captureIds.length,
      },
    );

    await toggleFixedStatus(
      dispatch,
      getState(),
      projectApiClient,
      isFixed,
      captureIds,
      setIsProcessing,
      onSuccess,
    );
  }, [
    isProcessing,
    dispatch,
    getState,
    projectApiClient,
    isFixed,
    entity.id,
    isClusterOrRoot,
    descendantScanIds,
    onSuccess,
  ]);

  const description = isFixed
    ? "Allows position and orientation to change\nduring registration or manual adjustment."
    : "Prevents changes to position and orientation\nduring registration or manual adjustment.";

  return (
    <FaroMenuItem
      key="fixRelease"
      label={isFixed ? "Release capture positions" : "Fix capture positions"}
      Icon={getFixReleaseMenuIcon(isProcessing, isFixed)}
      secondaryText={
        // Block + top margin keep the multi-line description spaced below the label;
        // the extra left padding aligns it with the label next to the 1.5rem icon.
        <FaroText
          variant="helpText"
          sx={{ display: "block", whiteSpace: "pre-line", mt: 1, pl: 1 }}
        >
          {description}
        </FaroText>
      }
      sx={{ "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
      onClick={onToggleFixedStatus}
      disabled={
        (disabled ?? false) || isProcessing || descendantScanIds.length === 0
      }
    />
  );
}

/**
 * @param isProcessing whether the fix/release request is currently in flight.
 * @param isFixed whether the capture positions are currently fixed.
 * @returns the icon component to show in the fix/release menu item.
 */
function getFixReleaseMenuIcon(
  isProcessing: boolean,
  isFixed: boolean,
): NonNullable<FaroMenuItemProps["Icon"]> {
  if (isProcessing) {
    return SpinnerIcon;
  }
  return isFixed ? ReleaseMenuIcon : FixMenuIcon;
}

/** @returns a spinner sized for the `Icon` slot of a FaroMenuItem */
function SpinnerIcon(): JSX.Element {
  return <CircularProgress size="1.25rem" />;
}

/**
 * @param props the icon props passed by the FaroMenuItem
 * @returns the fix icon, usable in the `Icon` slot of a FaroMenuItem
 */
function FixMenuIcon(props: Omit<FaroSvgIconProps, "source">): JSX.Element {
  return <FaroSvgIcon source={FixIcon} {...props} />;
}

/**
 * @param props the icon props passed by the FaroMenuItem
 * @returns the release icon, usable in the `Icon` slot of a FaroMenuItem
 */
function ReleaseMenuIcon(props: Omit<FaroSvgIconProps, "source">): JSX.Element {
  return <FaroSvgIcon source={ReleaseIcon} {...props} />;
}
